import { useEffect, useState } from "react";

const STORAGE_KEY = "movmo_demo_mock_mode";

// Reads ?mock=true / ?mock=false from the current URL and persists the choice
// in sessionStorage so navigation within the demo preserves it. Returns the
// resolved boolean. Re-runs when the URL changes.
//
// Activation rules:
//   - ?mock=true  → set sessionStorage flag, return true.
//   - ?mock=false → clear sessionStorage flag, return false.
//   - no param    → return whatever sessionStorage says (default false).
//
// This means a sales-engineer can flip an already-running demo into mock mode
// just by appending the query param, then navigate freely without re-passing
// it. Opening a fresh tab without the param goes back to the real path.
export function useMockMode(): boolean {
    const read = () => {
        if (typeof window === "undefined") return false;
        const params = new URLSearchParams(window.location.search);
        const param = params.get("mock");
        if (param === "true") {
            window.sessionStorage.setItem(STORAGE_KEY, "true");
            return true;
        }
        if (param === "false") {
            window.sessionStorage.removeItem(STORAGE_KEY);
            return false;
        }
        return window.sessionStorage.getItem(STORAGE_KEY) === "true";
    };

    const [mockMode, setMockMode] = useState<boolean>(read);

    // Re-evaluate on URL changes (handled at the demo level by re-rendering on
    // route changes — react-router pushes new history entries, which doesn't
    // fire `popstate`, so we listen for both `popstate` and a manual refresh
    // via the location.search dependency below).
    useEffect(() => {
        const handler = () => setMockMode(read());
        window.addEventListener("popstate", handler);
        return () => window.removeEventListener("popstate", handler);
    }, []);

    return mockMode;
}
